/**
 * Every LayoutSlot field for the selected slot, with schema issues inline, plus a
 * read-out of how OBS will draw it (from the shared geometry module).
 */
import type { Dispatch } from 'react';
import { OBS_BOUNDS_TYPES, slotGeometry, type LayoutSlot, type NameplateAnchor, type ValidationIssue } from '@ioda/shared';
import type { EditorAction, SlotPatch } from '../model/editor.ts';
import { AlignmentPicker, Field, NumberInput, TextInput } from './fields.tsx';

const BOUNDS_LABELS: Record<(typeof OBS_BOUNDS_TYPES)[number], string> = {
  OBS_BOUNDS_NONE: 'None (source size)',
  OBS_BOUNDS_STRETCH: 'Stretch to bounds',
  OBS_BOUNDS_SCALE_INNER: 'Scale inner (letterbox)',
  OBS_BOUNDS_SCALE_OUTER: 'Scale outer (fill + crop)',
  OBS_BOUNDS_SCALE_TO_WIDTH: 'Scale to width',
  OBS_BOUNDS_SCALE_TO_HEIGHT: 'Scale to height',
  OBS_BOUNDS_MAX_ONLY: 'Maximum size only',
};

const ANCHORS: readonly NameplateAnchor[] = ['inside-bottom-left', 'inside-bottom-center', 'below', 'above'];

const fmt = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));

interface Props {
  slot: LayoutSlot;
  index: number;
  issues: ValidationIssue[];
  dispatch: Dispatch<EditorAction>;
}

export function Inspector({ slot, index, issues, dispatch }: Props) {
  const set = (patch: SlotPatch) => dispatch({ type: 'updateSlot', slotId: slot.slotId, patch });
  const issuesFor = (...fields: string[]) =>
    issues.filter((i) => fields.some((f) => i.path === `slots.${index}.${f}` || i.path.startsWith(`slots.${index}.${f}.`))).map((i) => i.message);
  const g = slotGeometry(slot);
  const num = (field: keyof LayoutSlot, label: string, opts: { integer?: boolean; min?: number } = {}) => (
    <Field label={label} issues={issuesFor(field)}>
      <NumberInput value={slot[field] as number} integer={opts.integer ?? false} {...(opts.min !== undefined ? { min: opts.min } : {})} onChange={(v) => set({ [field]: v })} />
    </Field>
  );

  return (
    <div className="inspector">
      <h2>Slot</h2>
      <div className="grid2">
        <Field label="Slot ID (semantic name)" issues={issuesFor('slotId')} wide>
          <TextInput value={slot.slotId} placeholder="host, guest-1, speaker…" onChange={(v) => set({ slotId: v })} />
        </Field>
        {num('order', 'Order', { integer: true, min: 0 })}
        {num('zIndex', 'Z index', { integer: true })}
        <Field label="Parked (hidden)" wide>
          <input type="checkbox" checked={slot.hidden === true} onChange={(e) => set(e.target.checked ? { hidden: true } : { hidden: undefined })} />
        </Field>
      </div>

      <h2>Position</h2>
      <div className="grid2">
        {num('positionX', 'X')}
        {num('positionY', 'Y')}
        <Field label="Alignment (anchor at X/Y)" issues={issuesFor('alignment')} wide>
          <AlignmentPicker value={slot.alignment} onChange={(v) => set({ alignment: v })} />
        </Field>
        {num('rotation', 'Rotation °')}
      </div>

      <h2>Bounds</h2>
      <div className="grid2">
        <Field label="Bounds type" issues={issuesFor('boundsType')} wide>
          <select value={slot.boundsType} onChange={(e) => set({ boundsType: e.target.value as LayoutSlot['boundsType'] })}>
            {OBS_BOUNDS_TYPES.map((t) => (
              <option key={t} value={t}>
                {BOUNDS_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        {num('boundsWidth', 'Width', { min: 1 })}
        {num('boundsHeight', 'Height', { min: 1 })}
      </div>

      <h2>Crop (source px)</h2>
      <div className="grid2">
        {num('cropLeft', 'Left', { integer: true, min: 0 })}
        {num('cropRight', 'Right', { integer: true, min: 0 })}
        {num('cropTop', 'Top', { integer: true, min: 0 })}
        {num('cropBottom', 'Bottom', { integer: true, min: 0 })}
      </div>

      <h2>Nameplate hint</h2>
      <div className="grid2">
        <Field label="Anchor" issues={issuesFor('nameplate')} wide>
          <select
            value={slot.nameplate?.anchor ?? ''}
            onChange={(e) => set({ nameplate: e.target.value === '' ? undefined : { ...slot.nameplate, anchor: e.target.value as NameplateAnchor } })}
          >
            <option value="">None (bundle default)</option>
            {ANCHORS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Field>
        {slot.nameplate && (
          <>
            <Field label="Offset X">
              <NumberInput value={slot.nameplate.offsetX ?? 0} onChange={(v) => set({ nameplate: { ...slot.nameplate!, offsetX: v } })} />
            </Field>
            <Field label="Offset Y">
              <NumberInput value={slot.nameplate.offsetY ?? 0} onChange={(v) => set({ nameplate: { ...slot.nameplate!, offsetY: v } })} />
            </Field>
          </>
        )}
      </div>

      <h2>How OBS draws it</h2>
      <dl className="readout">
        <dt>Video at</dt>
        <dd>
          {fmt(g.contentBounds.x)}, {fmt(g.contentBounds.y)}
        </dd>
        <dt>Video size</dt>
        <dd>
          {fmt(g.contentBounds.width)} × {fmt(g.contentBounds.height)}
        </dd>
        <dt>Scale</dt>
        <dd>{g.scale.x.toFixed(4)}</dd>
        <dt>Source crop</dt>
        <dd>
          L {g.sourceCrop.left}, R {g.sourceCrop.right}, T {g.sourceCrop.top}, B {g.sourceCrop.bottom}
        </dd>
      </dl>
      <p className="note">Source is the 1920×1080 nested CAM-n scene. Sent with scale 1, centered bounds alignment, crop to bounds on.</p>
    </div>
  );
}
